import React, { ReactNode } from 'react'
import { Table, Pagination, Menu, ActionIcon, Box, Skeleton } from '@mantine/core'
import { FiMoreVertical } from 'react-icons/fi'
import EmptyState from './EmptyState'

export interface ColumnDef<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
}

export interface RowAction<T> {
  label: string;
  onClick: (row: T) => void;
  color?: string;
}

interface Props<T> {
  columns: ColumnDef<T>[];
  data: T[];
  pagination: { totalCount: number; pageNumber: number; pageSize: number };
  onPageChange: (page: number) => void;
  loading?: boolean;
  rowActions?: RowAction<T>[];
}

function getRowKey<T> (row: T, columns: ColumnDef<T>[]): string {
  const keyCol = columns[0]
  if (!keyCol) return String(Math.random())
  const val = (row as Record<string, unknown>)[keyCol.key]
  return val != null ? String(val) : String(Math.random())
}

export default function AdminTable<T> ({
  columns,
  data,
  pagination,
  onPageChange,
  loading = false,
  rowActions
}: Props<T>) {
  const { totalCount, pageNumber, pageSize } = pagination
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  if (loading) {
    return (
      <Table striped highlightOnHover>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
            {rowActions && rowActions.length > 0 && <th style={{ width: 48 }} />}
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4, 5].map((i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td key={col.key}>
                  <Skeleton height={20} />
                </td>
              ))}
              {rowActions && rowActions.length > 0 && (
                <td>
                  <Skeleton height={24} width={24} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    )
  }

  if (data.length === 0) {
    return (
      <EmptyState
        title="No results"
        message="Nothing to display."
      />
    )
  }

  return (
    <Box>
      <Table striped highlightOnHover>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
            {rowActions && rowActions.length > 0 && <th style={{ width: 48 }} />}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={getRowKey(row, columns)}>
              {columns.map((col) => (
                <td key={col.key}>
                  {col.render
                    ? col.render(row)
                    : String((row as Record<string, unknown>)[col.key] ?? '')}
                </td>
              ))}
              {rowActions && rowActions.length > 0 && (
                <td>
                  <Menu shadow="md" width={180} position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" size="sm">
                        <FiMoreVertical size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {rowActions.map((action, idx) => (
                        <Menu.Item
                          key={idx}
                          color={action.color}
                          onClick={() => action.onClick(row)}
                        >
                          {action.label}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <Pagination
          total={totalPages}
          page={pageNumber}
          onChange={onPageChange}
          position="right"
          mt="md"
        />
      )}
    </Box>
  )
}
